import { FC, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from '@mui/material';
import { StatsDateFilter } from '../analytics/common/StatsDateFilter';
import { useAiValidationSummary } from '../../hooks/useAiValidation';

// 단어 학습 언어 (SupportedLanguages 화이트리스트 11종)
const WORD_LANG_OPTIONS = [
    { value: 'en', label: '영어' },
    { value: 'ko', label: '한국어' },
    { value: 'ja', label: '일본어' },
    { value: 'zh', label: '중국어' },
    { value: 'vi', label: '베트남어' },
    { value: 'es', label: '스페인어' },
    { value: 'de', label: '독일어' },
    { value: 'fr', label: '프랑스어' },
    { value: 'it', label: '이탈리아어' },
    { value: 'pt', label: '포르투갈어' },
    { value: 'id', label: '인도네시아어' },
];

// 뜻 언어 (ARB 지원 4종)
const MEANING_LANG_OPTIONS = [
    { value: 'ko', label: '한국어' },
    { value: 'en', label: '영어' },
    { value: 'ja', label: '일본어' },
    { value: 'vi', label: '베트남어' },
];

// AiValidationFlag enum 한글 라벨
const FLAG_LABELS: Record<string, string> = {
    SCRIPT_MISMATCH_TERM: '단어 스크립트 불일치',
    SCRIPT_MISMATCH_TRANSLATION: '번역 스크립트 불일치',
    LIKELY_DEFINITION: '정의문 의심',
    MULTI_WORD_PHRASE: '다중 단어 구문',
    PUNCTUATION_HEAVY: '문장부호 과다',
    CASING_VIOLATION: '대소문자 규칙 위반',
};

// FallbackOutcome enum 한글 라벨 + 색상
const OUTCOME_META: Record<string, { label: string; color: 'success' | 'warning' | 'default' }> = {
    RESOLVED: { label: '재생성 해결', color: 'success' },
    STILL_FLAGGED: { label: '재생성 후에도 플래그', color: 'warning' },
    NOT_RETRIED: { label: '재시도 안 함', color: 'default' },
};

interface CountTableProps {
    title: string;
    counts: Record<string, number>;
    label: (key: string) => React.ReactNode;
}

const CountTable: FC<CountTableProps> = ({ title, counts, label }) => {
    const rows = Object.entries(counts).sort((a, b) => b[1] - a[1]);
    const total = rows.reduce((sum, [, n]) => sum + n, 0);
    return (
        <Card>
            <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, pt: 2, pb: 1 }}>
                    <Typography variant="h6">{title}</Typography>
                    <Chip size="small" label={`총 ${total.toLocaleString()}건`} />
                </Box>
                <TableContainer>
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>유형</TableCell>
                                <TableCell align="right">건수</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={2} align="center" sx={{ color: 'text.secondary', py: 3 }}>
                                        해당 기간 데이터 없음
                                    </TableCell>
                                </TableRow>
                            ) : (
                                rows.map(([key, n]) => (
                                    <TableRow key={key}>
                                        <TableCell>{label(key)}</TableCell>
                                        <TableCell align="right">{n.toLocaleString()}</TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </CardContent>
        </Card>
    );
};

const AiValidationSummary: FC = () => {
    const [range, setRange] = useState<{ from: string; to: string }>({ from: '', to: '' });
    const [wordLang, setWordLang] = useState('');
    const [meaningLang, setMeaningLang] = useState('');

    const { data, isLoading, isError, error } = useAiValidationSummary({
        from: range.from,
        to: range.to,
        wordLang: wordLang || undefined,
        meaningLang: meaningLang || undefined,
    });

    const flagCounts = data?.flagCounts ?? {};
    const fallbackOutcomes = data?.fallbackOutcomes ?? {};

    return (
        <Box sx={{ p: 3, maxWidth: 1100 }}>
            <Typography variant="h5" sx={{ mb: 0.5 }}>
                AI 검증 요약
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                AI 단어/이미지/주제 생성 결과의 자동 검증(스크립트 일치 등) 플래그 집계와 재생성 폴백 결과를 조회합니다.
            </Typography>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <StatsDateFilter onChange={(from, to) => setRange({ from, to })} defaultDays={7} />
                        <TextField
                            select
                            size="small"
                            label="단어 언어"
                            value={wordLang}
                            onChange={(e) => setWordLang(e.target.value)}
                            sx={{ width: 140 }}
                        >
                            <MenuItem value="">전체</MenuItem>
                            {WORD_LANG_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            select
                            size="small"
                            label="뜻 언어"
                            value={meaningLang}
                            onChange={(e) => setMeaningLang(e.target.value)}
                            sx={{ width: 140 }}
                        >
                            <MenuItem value="">전체</MenuItem>
                            {MEANING_LANG_OPTIONS.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </TextField>
                    </Box>
                </CardContent>
            </Card>

            {isError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error?.message ?? '요약 조회에 실패했습니다.'}
                </Alert>
            )}

            {data?.note && (
                <Alert severity="info" sx={{ mb: 2 }}>
                    <strong>{data.scope}</strong> — {data.note}
                </Alert>
            )}

            {isLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <CountTable
                            title="검증 플래그 집계"
                            counts={flagCounts}
                            label={(key) => FLAG_LABELS[key] ?? key}
                        />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <CountTable
                            title="재생성 폴백 결과"
                            counts={fallbackOutcomes}
                            label={(key) => {
                                const meta = OUTCOME_META[key];
                                return meta ? <Chip size="small" color={meta.color} label={meta.label} /> : key;
                            }}
                        />
                    </Grid>
                </Grid>
            )}
        </Box>
    );
};

export default AiValidationSummary;
